/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { ChartType } from "~/generated/graphql";
import {
  DateRange,
  EstatePeriod,
  Granularity,
  Period,
  ChartType as CostAnalysisChartType,
} from "~/components";
import { EstateGroupBy, APPLICATIONS_SORT_OPTIONS } from "./components";
import {
  getEndDate,
  getStartDate,
  useEstateRecordsGroupByChartJob,
  TotalCostTable,
  EstateRecordsGroupingChart,
  EstateRecordsGroupingTable,
  useEstateRecordsCost,
} from "./components/Charts";
import {
  isTableDataSortable,
  mapEstatePeriodToPeriod,
  sortDataByTotal,
} from "./helpers";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  granularity: Granularity;
  setIsDataLoading: (isLoading: boolean) => void;
};

export function EstateRecordsApplicationsView({
  period,
  range,
  granularity,
  setIsDataLoading,
}: Props) {
  const startDate = getStartDate(period, range);
  const endDate = getEndDate(period, range);

  const {
    isChartDataLoading,
    isTableDataLoading,
    isDataLoading,
    chartData,
    tableData,
    tableError,
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  } = useEstateRecordsGroupByChartJob({
    chartType: ChartType.Application,
    startDate,
    endDate,
  });

  useEffect(() => {
    setIsDataLoading(isDataLoading);
  }, [isDataLoading, setIsDataLoading]);

  const mappedPeriod = mapEstatePeriodToPeriod(period);

  const {
    costPerGroupChartData,
    costPerGroupTableData,
    tableCount,
    extraDetails,
    allColumns,
    stackBarColors,
    chartKeys,
  } = useEstateRecordsCost({
    chartData,
    tableData,
    granularity,
    period: mappedPeriod,
    startDate,
    endDate,
    chartType: CostAnalysisChartType.COST_PER_APPLICATION,
  });

  const sortedCostPerAppTableData = isTableDataSortable(sortField, period)
    ? sortDataByTotal(costPerGroupTableData, sortDirection)
    : costPerGroupTableData;

  const firstColumnWidth = mappedPeriod === Period.MONTH ? "30%" : undefined;

  return (
    <div>
      <EstateRecordsGroupingChart
        data={costPerGroupChartData}
        granularity={granularity}
        loading={isChartDataLoading}
        startDate={startDate}
        endDate={endDate}
        period={period}
        colors={stackBarColors}
        keys={chartKeys}
        chartType={CostAnalysisChartType.COST_PER_APPLICATION}
      />
      <TotalCostTable
        period={period}
        range={range}
        count={tableData?.chartJobTable?.data?.count ?? 0}
        isLoading={isTableDataLoading}
        chartType={EstateGroupBy.APP}
        totalTop={extraDetails?.totalTop ?? "-"}
        totalOther={extraDetails?.totalOther ?? "-"}
      />
      <EstateRecordsGroupingTable
        loading={isTableDataLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        sortOptions={APPLICATIONS_SORT_OPTIONS}
        tableCount={tableCount ?? 0}
        columns={allColumns}
        data={sortedCostPerAppTableData}
        firstColumnWidth={firstColumnWidth}
        error={tableError}
        period={period}
        onSortSelect={onSortSelect}
        onSortDirectionSelect={onSortOrderChange}
      />
    </div>
  );
}
